/**
 * TypographyV2 — Tailwind-only Contentful rich-text renderer.
 *
 * Drop-in replacement for the original `<Typography>` that removes every
 * MUI / Ascend dependency.  All styling is expressed through Tailwind
 * utility classes derived from the achieve-theme typography utilities
 * (bodyLg, displayXl, headingLg, etc.).
 *
 * ## Migration notes
 *
 * | Old prop (MUI)       | New prop (Tailwind)           |
 * |----------------------|-------------------------------|
 * | `styleOverride`      | `classNameOverride`           |
 * | `variantOverride`    | `variantOverride` (unchanged) |
 * | `mobileVariantOverride` | `mobileVariantOverride`    |
 * | `sx` on sub-nodes    | className strings             |
 *
 * `classNameOverride` uses the same key structure as the old `styleOverride`
 * (keyed by rich-text block type) but the values are Tailwind class strings
 * instead of MUI `sx` objects.
 */

import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { BLOCKS, MARKS, INLINES } from '@contentful/rich-text-types'
import { MediaImageStandard, SuperScript } from 'components/Contentful'
import { _get } from 'utils/shared'
import { BlockQuote } from 'components/BlockQuote'
import { rewriteAssetUrl } from 'utils/conversions'
import classNames from 'classnames'
import { NO_CRAWL_ID_PLACEHOLDER_REGEX } from 'constants/regex'

import { RICH_TEXT, VARIANT_CLASS_MAP, BASE_MARGIN_RESET } from './variantMap'
import {
  resolveVariantClass,
  resolveResponsiveVariantClass,
  resolveClassNameOverride,
} from './variantUtils'
import { handleContentToMapper, parseRichTextLineBreak } from './contentHelpers'

/**
 * Build a variantOverride map that forces every block type that has a default
 * variant in RICH_TEXT to use the given `variant`.
 *
 * This lets callers write `<TypographyV2 variant="displayXl" />` as a shorthand
 * instead of manually building `variantOverride={{ paragraph: 'displayXl', ... }}`.
 *
 * The HTML element is NOT changed — it is still determined by the rich-text
 * node type (e.g. a `paragraph` node still renders as `<p>`).
 */

/**
 * Sentinel value used to suppress variant classes when an unrecognised
 * variant string (e.g. "normal", "eyebrow") is passed explicitly.
 *
 * In the old MUI Typography, unknown variant values like "normal" caused MUI
 * to apply no special font styling, letting the consumer `className` classes
 * fully control sizing.  We replicate that by mapping every block type to
 * this sentinel, which `resolveVariantClass` / `resolveResponsiveVariantClass`
 * won't find in VARIANT_CLASS_MAP, so no variant class is emitted.
 */
const VARIANT_NONE = '__none__'

function buildVariantOverrideFromProp(variant) {
  // No variant prop at all → let RICH_TEXT defaults (bodyLg etc.) apply
  if (!variant) return undefined

  // Recognised variant → build a full override so every block type uses it
  if (VARIANT_CLASS_MAP[variant]) {
    const override = {}
    for (const [blockType, config] of Object.entries(RICH_TEXT)) {
      if (config.variant) {
        override[blockType] = variant
      }
    }
    return override
  }

  // Unrecognised variant (e.g. "normal", "eyebrow") → suppress all default
  // variant classes so that only the consumer className controls styling.
  const override = {}
  for (const [blockType, config] of Object.entries(RICH_TEXT)) {
    if (config.variant) {
      override[blockType] = VARIANT_NONE
    }
  }
  return override
}

import { HyperLinkV2 } from './HyperLinkV2'
import { ScrollLinkV2, ScrollToMarkerV2 } from './ScrollLinkV2'
import { UncrawlableLinkV2 } from './UncrawlableLinkV2'
import { VideoDialogV2 } from './VideoDialogV2'
import { ClientSideTypographyV2 } from './ClientSideTypographyV2'

// ── Font-weight prop → Tailwind class mapping ──────────────────────────────
const FONT_WEIGHT_CLASS_MAP = {
  light: 'font-light',
  regular: 'font-normal',
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
  extrabold: 'font-extrabold',
}

function resolveFontWeightClass(fontWeight) {
  if (!fontWeight) return ''
  return FONT_WEIGHT_CLASS_MAP[fontWeight] ?? ''
}

// ── String content shortcut ─────────────────────────────────────────────────

function TypographyStringType({
  classNameOverride,
  variantOverride,
  mobileVariant,
  mobileVariantOverride,
  typographyContent,
  richText = RICH_TEXT,
  responsive,
  fontWeight,
  props,
}) {
  const blockType = 'paragraph'

  const variantCls = responsive
    ? resolveResponsiveVariantClass(
        blockType,
        mobileVariant,
        mobileVariantOverride,
        variantOverride,
        richText
      )
    : resolveVariantClass(blockType, variantOverride, richText)

  const overrideCls = resolveClassNameOverride(blockType, classNameOverride)
  const fontWeightCls = resolveFontWeightClass(fontWeight)

  // Extract className and component from props to avoid passing them as invalid HTML attributes.
  // The `component` prop replicates MUI Typography's element override behaviour.
  const { className: propsClassName, component: propsComponent, ...restProps } = props
  const Component = propsComponent ?? richText[blockType]?.component ?? 'p'

  return (
    <Component
      className={classNames(
        BASE_MARGIN_RESET,
        variantCls,
        overrideCls,
        fontWeightCls,
        propsClassName
      )}
      {...restProps}
    >
      {typographyContent}
    </Component>
  )
}

// ── Default render-node factory ─────────────────────────────────────────────

function createRenderNodeDefault({
  children,
  blockType,
  responsive,
  mobileVariant,
  variantOverride,
  mobileVariantOverride,
  classNameOverride,
  className,
  richText = RICH_TEXT,
  propsComponent,
  inline = false,
  fontWeight,
}) {
  const nodeType = BLOCKS[blockType]

  const variantCls = responsive
    ? resolveResponsiveVariantClass(
        nodeType,
        mobileVariant,
        mobileVariantOverride,
        variantOverride,
        richText
      )
    : resolveVariantClass(nodeType, variantOverride, richText)

  const overrideCls = resolveClassNameOverride(nodeType, classNameOverride)
  const fontWeightCls = resolveFontWeightClass(fontWeight)

  // Override component to 'span' when inline + paragraph
  // Extract `component` from propsComponent so it can override the element
  // (replicates MUI Typography's `component` prop behaviour).
  const {
    className: propsClassName,
    component: propsComponentOverride,
    ...restProps
  } = propsComponent ?? {}
  const Component =
    inline && blockType === 'PARAGRAPH'
      ? 'span'
      : propsComponentOverride ?? richText[nodeType]?.component ?? 'div'

  return (
    <Component
      className={classNames(
        BASE_MARGIN_RESET,
        variantCls,
        overrideCls,
        fontWeightCls,
        className,
        propsClassName
      )}
      {...restProps}
    >
      {parseRichTextLineBreak(children)}
    </Component>
  )
}

// ── Rich-text renderer options ──────────────────────────────────────────────

function buildRenderOptions({
  createRenderNode,
  props,
  classNameOverride,
  variantOverride,
  richText = RICH_TEXT,
}) {
  return {
    // ── Marks ──
    renderMark: {
      [MARKS.BOLD]: (text) => <strong {...props}>{text}</strong>,
      [MARKS.ITALIC]: (text) => <em {...props}>{text}</em>,
    },

    // ── Nodes ──
    renderNode: {
      // Paragraphs
      [BLOCKS.PARAGRAPH]: (node, children) => {
        const onlyOneText =
          Array.isArray(node.content) &&
          node.content.length === 1 &&
          node.content[0]?.nodeType === 'text' &&
          typeof node.content[0]?.value === 'string'

        if (onlyOneText) {
          const val = node.content[0].value
          const m = val.match(NO_CRAWL_ID_PLACEHOLDER_REGEX)
          if (m) {
            return createRenderNode(
              node,
              [],
              'PARAGRAPH',
              'font-normal mb-[20px] scroll-mt-[72px] md:text-sm leading-[170%] md:leading-[170%] [&_a]:[font-size:inherit]',
              false,
              false,
              { id: m[0], 'no-crawl-id': m[0] }
            )
          }
        }
        return createRenderNode(node, children, 'PARAGRAPH')
      },

      // Headings
      [BLOCKS.HEADING_1]: (node, children) => createRenderNode(node, children, 'HEADING_1'),
      [BLOCKS.HEADING_2]: (node, children) => createRenderNode(node, children, 'HEADING_2'),
      [BLOCKS.HEADING_3]: (node, children) => createRenderNode(node, children, 'HEADING_3'),
      [BLOCKS.HEADING_4]: (node, children) => createRenderNode(node, children, 'HEADING_4'),
      [BLOCKS.HEADING_5]: (node, children) => createRenderNode(node, children, 'HEADING_5'),
      [BLOCKS.HEADING_6]: (node, children) => createRenderNode(node, children, 'HEADING_6'),

      // Lists
      [BLOCKS.OL_LIST]: (_node, children) => (
        <ol className="typography-ol list-decimal mb-8 [&>li>ol]:list-[upper-alpha] [&>li>ol>li>ol]:list-[lower-roman] [&>li>ol>li>ol>li>ol]:list-[lower-alpha] [&_li_p:only-of-type]:mb-0 [&_li_.typography-ol]:mb-0 [&_li_.typography-ul]:mb-0">
          {children}
        </ol>
      ),
      [BLOCKS.UL_LIST]: (_node, children) => (
        <ul className="typography-ul mb-8 [&_li_.typography-ul]:mb-0 [&_li_.typography-ol]:mb-0">
          {children}
        </ul>
      ),
      [BLOCKS.LIST_ITEM]: (_node, children) => (
        <li className="mb-1 [&_li_p:last-child]:mb-0">{children}</li>
      ),

      // Horizontal rule — matches MUI Divider: border-width:0, border-bottom-width:thin, border-style:solid
      [BLOCKS.HR]: () => (
        <hr
          className={classNames(
            'm-0 mb-8 border-0 border-b border-solid border-neutral-150',
            resolveClassNameOverride('hr', classNameOverride)
          )}
          {...props}
        />
      ),

      // Embedded asset (image)
      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        const imageWidth = _get(node, 'data.target.fields.file.details.image.width')
        const imageHeight = _get(node, 'data.target.fields.file.details.image.height')

        return (
          <span className="items-center flex justify-center">
            <MediaImageStandard
              content={node?.data.target}
              width={imageWidth}
              height={imageHeight}
              className="object-contain h-full w-full"
              {...props}
            />
            <p />
          </span>
        )
      },

      // Table
      [BLOCKS.TABLE]: (node, children) => {
        const fewColumns = node?.content[0]?.content?.length < 4

        return (
          <div>
            <div
              className={classNames(
                'overflow-x-visible border-collapse h-auto mb-8 [&_td_p:last-child]:mb-0 [&_th_p:last-child]:mb-0',
                fewColumns &&
                  'md:[&_td]:w-auto md:[&_th]:w-auto [&_td]:text-center [&_th]:text-center'
              )}
            >
              <table
                className={classNames(
                  'table-auto w-full border-collapse',
                  resolveVariantClass('table', variantOverride, richText),
                  resolveClassNameOverride('table', classNameOverride)
                )}
                {...props}
              >
                {children}
              </table>
            </div>
          </div>
        )
      },

      [BLOCKS.TABLE_ROW]: (node, children) => {
        const childrenNodeTypes = node?.content?.map((c) => c?.nodeType) ?? []
        if (childrenNodeTypes.includes(BLOCKS.TABLE_HEADER_CELL)) {
          return (
            <thead>
              <tr>{children}</tr>
            </thead>
          )
        }
        return (
          <tbody>
            <tr>{children}</tr>
          </tbody>
        )
      },

      [BLOCKS.TABLE_CELL]: (_node, children) => (
        <td className="h-auto border border-solid border-[#DCDCDC] border-collapse py-[6px] px-4">
          {children}
        </td>
      ),

      [BLOCKS.TABLE_HEADER_CELL]: (_node, children) => (
        <th className="h-auto py-[6px] px-4 font-bold">{children}</th>
      ),

      // Hyperlinks
      [INLINES.HYPERLINK]: (node, children) => (
        <HyperLinkV2
          node={node}
          variantClass={resolveVariantClass('hyperlink', variantOverride, richText)}
          classNameOverride={resolveClassNameOverride('hyperlink', classNameOverride)}
          {...props}
        >
          {children}
        </HyperLinkV2>
      ),

      [INLINES.ASSET_HYPERLINK]: (node, children) => {
        const linkId = node?.content[0]?.value
        const url = rewriteAssetUrl(node?.data?.target?.fields?.file?.url || node?.data?.url)

        return (
          <a
            target="_blank"
            rel="noopener noreferrer"
            className={classNames(
              resolveVariantClass('asset-hyperlink', variantOverride, richText),
              resolveClassNameOverride('asset-hyperlink', classNameOverride)
            )}
            data-testid={`Link-${url}-${linkId}`}
            href={rewriteAssetUrl(url)}
            aria-label={`${children} - link opens in a new tab`}
            {...props}
          >
            {children}
          </a>
        )
      },

      // Inline entries
      [INLINES.EMBEDDED_ENTRY]: (node) => {
        const { plainText, styledText, variation, identifier, videoPlayer, uiComponent } = _get(
          node?.data?.target?.fields ? node.data.target.fields : node,
          'data',
          {}
        )
        const { contentType } = _get(node, 'data.target.sys', {})

        if (styledText === 'superscript') {
          return <SuperScript text={plainText} variation={variation} {...props} />
        }
        if (styledText === 'scrollTo') {
          return <ScrollToMarkerV2 identifier={identifier} />
        }
        if (styledText === 'scrollFrom') {
          return (
            <ScrollLinkV2
              plainText={plainText}
              identifier={identifier}
              variantClass={resolveVariantClass('asset-hyperlink', variantOverride, richText)}
              classNameOverride={resolveClassNameOverride('asset-hyperlink', classNameOverride)}
              {...props}
            />
          )
        }
        if (videoPlayer && node?.data?.__typename === 'VideoModal') {
          return <VideoDialogV2 content={_get(node, 'data', {})} />
        }
        if (contentType?.sys?.id === 'uncrawlableLink') {
          return (
            <UncrawlableLinkV2
              node={node}
              variantClass={resolveVariantClass('asset-hyperlink', variantOverride, richText)}
              classNameOverride={resolveClassNameOverride('asset-hyperlink', classNameOverride)}
            />
          )
        }
        if (uiComponent === 'Typography') {
          return (
            <TypographyV2
              {...props}
              component="span"
              content={node?.data?.textContent}
              variantOverride={variantOverride}
              classNameOverride={classNameOverride}
            />
          )
        }
        if (uiComponent === 'TypographyClientSide') {
          return (
            <ClientSideTypographyV2
              textContent={node?.data?.textContent}
              variantOverride={variantOverride}
              classNameOverride={classNameOverride}
              TypographyComponent={TypographyV2}
              {...props}
            />
          )
        }
        return null
      },

      // Block quote
      [BLOCKS.QUOTE]: (node, children) => <BlockQuote text={node.content}>{children}</BlockQuote>,
    },

    // ── renderText ──
    renderText: (text) => {
      const hasPlaceholder = typeof text === 'string' && /\[\(ACX-[^)]+\)\]/.test(text)
      if (
        typeof text === 'string' &&
        (text.includes('<svg') ||
          text.includes('<b>') ||
          text.includes('<strong>') ||
          hasPlaceholder)
      ) {
        const parts = []
        let key = 0
        const htmlRegex =
          /(<svg[\s\S]*?<\/svg>|<b>[\s\S]*?<\/b>|<strong>[\s\S]*?<\/strong>|\[\(ACX-[^)]+\)\])/g
        const segments = text.split(htmlRegex)

        segments.forEach((segment) => {
          const trimmed = segment.trim()
          if (trimmed.startsWith('<svg')) {
            parts.push(
              <span
                key={`svg-${key++}`}
                className="inline-flex items-center align-middle mx-1"
                dangerouslySetInnerHTML={{ __html: segment }}
              />
            )
          } else if (trimmed.startsWith('<b>') || trimmed.startsWith('<strong>')) {
            const boldText = segment.replace(/<\/?b>|<\/?strong>/g, '')
            const placeholderMatch = boldText.match(/\[\(ACX-[^)]+\)\]/)
            if (placeholderMatch) {
              const idx = boldText.indexOf(placeholderMatch[0])
              const before = boldText.slice(0, idx)
              const after = boldText.slice(idx + placeholderMatch[0].length)
              parts.push(
                <strong key={`bold-${key++}`}>
                  {before}
                  <span id={placeholderMatch[0]} no-crawl-id={placeholderMatch[0]}>
                    {placeholderMatch[0]}
                  </span>
                  {after}
                </strong>
              )
            } else {
              parts.push(<strong key={`bold-${key++}`}>{boldText}</strong>)
            }
          } else if (/^\[\(ACX-[^)]+\)\]$/.test(trimmed)) {
            parts.push(
              <span key={`acx-${key++}`} id={trimmed} no-crawl-id={trimmed}>
                {trimmed}
              </span>
            )
          } else if (segment) {
            parts.push(segment)
          }
        })

        return parts.length > 0 ? parts : text
      }
      return text
    },
  }
}

// ── Main component ──────────────────────────────────────────────────────────

/**
 * Render Contentful Typography using pure Tailwind (no MUI).
 *
 * @param {Object}  content              – Contentful rich-text object (json / fields / nodeType)
 * @param {string}  variant              – Shorthand: apply this variant to ALL block types (e.g. 'displayXl').
 *                                          The HTML element is NOT changed — only the styling class.
 *                                          Ignored when `variantOverride` is provided.
 * @param {Object}  variantOverride      – Keyed by block type → variant name override (takes precedence over `variant`)
 * @param {Object}  mobileVariantOverride – Keyed by block type → mobile variant name override
 * @param {Object}  classNameOverride    – Keyed by block type → Tailwind class string (replaces MUI `styleOverride`)
 * @param {string}  mobileVariant        – Explicit mobile variant name
 * @param {boolean} responsive           – Whether to render responsive (mobile + desktop) variants
 * @param {boolean} inline               – Render in inline mode (filters out block-level elements)
 * @param {string}  component            – Override root HTML element
 */
function TypographyV2({
  content: typographyContent = '',
  variant,
  variantOverride: variantOverrideProp,
  mobileVariantOverride,
  classNameOverride,
  mobileVariant,
  responsive,
  inline,
  fontWeight,
  ...props
}) {
  // When `variant` is provided as a shorthand, build a full override map so
  // every block type inherits that variant.  An explicit `variantOverride`
  // prop takes precedence for individual keys.
  const variantOverride = variantOverrideProp ?? buildVariantOverrideFromProp(variant)

  // ── String shortcut ──
  if (typeof typographyContent === 'string') {
    return (
      <TypographyStringType
        classNameOverride={classNameOverride}
        variantOverride={variantOverride}
        mobileVariantOverride={mobileVariantOverride}
        typographyContent={typographyContent}
        mobileVariant={mobileVariant}
        responsive={responsive}
        fontWeight={fontWeight}
        props={props}
      />
    )
  }

  // ── Object content (Contentful rich-text) ──
  const { textContent } = handleContentToMapper(typographyContent, inline)

  const createRenderNode = (
    node,
    children,
    blockType,
    className = 'typography-default',
    // variantToChild/styleToChild are accepted for call-site compat but unused in Tailwind version
    _unused1,
    _unused2,
    extraProps = {}
  ) => {
    const fallbackTestId = _get(node, 'content[0].value')
      ? `${_get(node, 'content[0].value')}`.slice(0, 50)
      : 'no-id-needed'

    const propsComponent = {
      'data-testid': fallbackTestId,
      'no-crawl-id': extraProps['no-crawl-id'] ?? undefined,
      ...props,
    }

    return createRenderNodeDefault({
      children,
      blockType,
      variantOverride,
      mobileVariantOverride,
      classNameOverride,
      className,
      mobileVariant,
      responsive,
      propsComponent,
      inline,
      fontWeight,
    })
  }

  const options = buildRenderOptions({
    createRenderNode,
    props,
    classNameOverride,
    variantOverride,
  })

  return textContent?.nodeType || textContent?.data
    ? documentToReactComponents(textContent || [], options)
    : null
}

export default TypographyV2
export { TypographyV2, TypographyStringType, createRenderNodeDefault, buildRenderOptions }
